import Phaser from 'phaser';
import {
  STAR_COLORS,
  defToProject,
  displayStars,
  hambiLabel,
  verifiedLabel,
} from '../../config/creator';
import { HUB_MENU_TRACK } from '../../config/levels';
import { accountClient } from '../../account/accountUi';
import { exploreGet, explorePlay, type ExploreLevel } from '../../account/explore';
import { drawStarfield } from '../starfield';
import { getMusicService } from '../audio/MusicService';
import {
  CREATOR_ORANGE,
  creatorButton,
  creatorFlash,
  creatorHeader,
  levelPreview,
} from '../creator/creatorUi';
import { INFO_BLUE, VERIFIED_GREEN, startExploreRun } from './ExploreScene';

export const NO_DESCRIPTION = 'The maker has not written a description.';

/** "2026-11-03" → "November 3, 2026" (a date only, never a time). */
export function approvedDateText(isoDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return 'Unknown';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/** The pieces a maker placed — every decoration, tower, enemy and textbox. */
export function objectCount(def: Record<string, unknown>): number {
  return Array.isArray(def.pieces) ? def.pieces.length : 0;
}

export function waveCount(def: Record<string, unknown>): number {
  return Array.isArray(def.waves) ? def.waves.length : 0;
}

/**
 * A published level's information page (spec 2026-11-03 §3), opened by the
 * blue square on its Explore row. Top: the name, its stars and Hambis (and
 * VERIFIED). Middle: the map, how many waves, how many objects the maker
 * placed, and when an admin first approved it and it went up. Bottom: the
 * maker's description — the approved one. No rewards are shown here.
 */
export class ExploreInfoScene extends Phaser.Scene {
  private code = '';
  private busy = false;
  /** The level as loaded (read by tests). */
  level: ExploreLevel | null = null;

  constructor() {
    super({ key: 'ExploreInfoScene' });
  }

  init(data: { code?: string }): void {
    this.code = data?.code ?? '';
    this.sys.settings.data = {};
  }

  create(): void {
    this.busy = false;
    this.level = null;
    drawStarfield(this, 1280, 720);
    creatorHeader(this, 'LEVEL INFO', () => this.scene.start('ExploreScene'));
    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => music.stop());
    const status = this.add
      .text(640, 380, 'Loading…', { fontSize: '26px', color: '#e8f4f1', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName('info-status');
    const client = accountClient();
    if (!client || !this.code) {
      status.setText('Explore needs the online servers');
      return;
    }
    void exploreGet(client, this.code).then((r) => {
      if (!this.sys.isActive()) return;
      if (!r.ok) {
        status.setText(r.error);
        return;
      }
      status.destroy();
      this.level = r.value;
      this.draw(r.value);
    });
  }

  private draw(l: ExploreLevel): void {
    // ── Top: the name and its ratings ──
    this.add
      .text(640, 108, l.name, { fontSize: '40px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName('info-name');
    this.add
      .text(640, 146, `by ${l.username}   #${l.code}`, { fontSize: '17px', color: '#cfe0bd' })
      .setOrigin(0.5);
    const stars = displayStars(l.plays, l.starOverride);
    const starText = this.add
      .text(560, 188, '★'.repeat(stars.count) + '☆'.repeat(5 - stars.count), {
        fontSize: '30px',
        color: STAR_COLORS[stars.color],
      })
      .setOrigin(1, 0.5)
      .setName('info-stars');
    const verified = verifiedLabel(l.verified);
    if (verified) {
      this.add
        .text(starText.x - starText.width - 18, 188, verified, {
          fontSize: '18px',
          color: VERIFIED_GREEN,
          fontStyle: 'bold',
        })
        .setOrigin(1, 0.5)
        .setName('info-verified');
    }
    const hx = 600;
    if (l.hambis && this.textures.exists('tower-patty-1')) {
      for (let h = 0; h < l.hambis; h++) {
        const icon = this.add.image(hx + 16 + h * 34, 188, 'tower-patty-1');
        icon.setScale(30 / Math.max(icon.width, icon.height));
      }
    }
    this.add
      .text(l.hambis ? hx + 16 + l.hambis * 34 : hx, 188, hambiLabel(l.hambis), {
        fontSize: '20px',
        color: l.hambis ? '#ffd23f' : '#9aa6ad',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setName('info-hambis');

    // ── Middle: the map and the facts ──
    const preview = levelPreview(this, defToProject(l.name, l.def), 560);
    preview.setPosition(380, 380);
    const facts: [string, string, string][] = [
      ['WAVES', String(waveCount(l.def)), 'info-waves'],
      ['OBJECTS', String(objectCount(l.def)), 'info-objects'],
      ['APPROVED AND POSTED', approvedDateText(l.approvedOn), 'info-approved'],
    ];
    facts.forEach(([label, value, name], i) => {
      const y = 262 + i * 82;
      this.add.rectangle(930, y + 18, 420, 70, INFO_BLUE, 0.18).setStrokeStyle(2, INFO_BLUE, 0.9);
      this.add
        .text(740, y, label, { fontSize: '15px', color: '#9fc6ff', fontStyle: 'bold' })
        .setOrigin(0, 0.5)
        .setLetterSpacing(2);
      this.add
        .text(740, y + 28, value, { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0, 0.5)
        .setName(name);
    });

    // ── Bottom: the description ──
    this.add
      .text(110, 548, 'DESCRIPTION', { fontSize: '16px', color: '#9aa6ad', fontStyle: 'bold' })
      .setLetterSpacing(2);
    this.add.rectangle(530, 628, 840, 120, 0x15131f, 0.9).setStrokeStyle(2, INFO_BLUE, 0.6);
    this.add
      .text(124, 578, l.description || NO_DESCRIPTION, {
        fontSize: '18px',
        color: l.description ? '#ffffff' : '#9aa6ad',
        fontStyle: l.description ? 'normal' : 'italic',
        wordWrap: { width: 810, useAdvancedWrap: true },
        lineSpacing: 4,
      })
      .setName('info-description');

    creatorButton(
      this,
      1110,
      628,
      'PLAY',
      { name: 'info-play', fill: CREATOR_ORANGE, width: 200, height: 64, fontSize: 28 },
      () => this.play(l),
    );
  }

  private play(l: ExploreLevel): void {
    const client = accountClient();
    if (this.busy || !client) return;
    this.busy = true;
    if (!startExploreRun(this, l)) {
      this.busy = false;
      creatorFlash(this, "This level can't be played");
      return;
    }
    void explorePlay(client, l.code);
  }
}
